"use client";

import { CinematicModal } from "@/components/ui/CinematicModal";
import { normalizePaywallSource } from "@/lib/analytics/paywall-source";
import { trackUpgradeIntent } from "@/lib/analytics/posthog-helpers";
import {
  cinematicModalPrimaryButtonClass,
  cinematicModalSubtitleClass,
  cinematicModalTitleClass,
} from "@/lib/cinematicModalStyles";
import { buildTrackedPaywallUrl } from "@/lib/paywallUrl";
import { useRouter } from "next/navigation";
import { usePostHog } from "posthog-js/react";

export type ProFeatureUpgradeModalFeature = "resume_session" | "premium_voice";

const FEATURE_COPY: Record<ProFeatureUpgradeModalFeature, { title: string; description: string }> =
  {
    resume_session: {
      title: "Resume your last session",
      description: "Upgrade to Pro to continue from the conversation where you left off.",
    },
    premium_voice: {
      title: "Unlock premium voices",
      description: "Upgrade to Pro to choose from every ChickyTutor voice.",
    },
  };

type ProFeatureUpgradeModalProps = {
  isOpen: boolean;
  feature: ProFeatureUpgradeModalFeature;
  onClose: () => void;
  source: string;
  userId?: string;
  userEmail?: string;
};

export function ProFeatureUpgradeModal({
  isOpen,
  feature,
  onClose,
  source,
  userId,
  userEmail,
}: ProFeatureUpgradeModalProps) {
  const router = useRouter();
  const posthog = usePostHog();
  const copy = FEATURE_COPY[feature];

  const handleUpgrade = () => {
    const paywallSource = normalizePaywallSource(source);
    trackUpgradeIntent(posthog, source, {
      user_id: userId,
      email: userEmail,
      feature,
      paywall_source: paywallSource,
    });
    router.push(buildTrackedPaywallUrl({ source: paywallSource }));
  };

  return (
    <CinematicModal isOpen={isOpen} onClose={onClose} titleId={`${source}-upgrade-dialog-title`}>
      <div className="pr-8">
        <h2 id={`${source}-upgrade-dialog-title`} className={cinematicModalTitleClass}>
          {copy.title}
        </h2>
        <p className={cinematicModalSubtitleClass}>ChickyTutor Pro</p>
      </div>

      <p className="mt-4 text-sm leading-relaxed text-white/75">{copy.description}</p>

      <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <button
          type="button"
          onClick={onClose}
          className="inline-flex h-11 items-center justify-center px-3 text-sm font-semibold text-white/55 transition-colors hover:text-white"
        >
          Maybe later
        </button>
        <button type="button" onClick={handleUpgrade} className={cinematicModalPrimaryButtonClass}>
          Upgrade to PRO
        </button>
      </div>
    </CinematicModal>
  );
}
